import { useState } from 'react';
import { toast } from 'react-toastify';
import { store } from '../../../redux/oldStore';
import { ServerResponse } from '../../../utils/Server';
import Icon, { IconType } from '../../Icon/Icon';
import Modal from '../../Modal/Modal';
import { sanitizeHtml } from '../../../utils/Tools';
import { IConfigItemSummary } from '../../Setup/ConfigForm/ConfigFormTypes';
import { IFlexTemplateSummary } from '../FlexFlowTypes';

interface IProps {
  onInstalled: (item: IConfigItemSummary) => void;
}

const FlexFlowAddFromTemplate = (props: IProps) => {
  const [show, setShow] = useState(false);
  const [templates, setTemplates] = useState<IFlexTemplateSummary[]>();
  const [busyId, setBusyId] = useState<string>();

  const open = () => {
    toast.dismiss();
    store.server.postApi<ServerResponse<IFlexTemplateSummary[]>>(`../FlexManager/Templates`, {}).then((x) => {
      if (x.Success) {
        setTemplates(x.Value ?? []);
        setShow(true);
      } else {
        toast.error(x.Message, { autoClose: false });
      }
    });
  };

  const install = (templateId: string, mode: 'as-is' | 'copy') => {
    toast.dismiss();
    setBusyId(`${templateId}:${mode}`);
    store.server
      .postApi<ServerResponse<IConfigItemSummary>>(`../FlexManager/InstallTemplate`, { templateId, mode })
      .then((x) => {
        setBusyId(undefined);
        if (x.Success) {
          toast.success(mode === 'as-is' ? 'Template installed.' : 'Template copied.');
          setShow(false);
          props.onInstalled(x.Value);
        } else {
          toast.error(x.Message, { autoClose: false });
        }
      });
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-default btn-xs"
        data-testid="flex-add-from-template"
        onClick={open}
      >
        <Icon type={IconType.copy} /> Add from template
      </button>

      {show && (
        <Modal setModalOpen={setShow} size="l" title="Add from template">
          <div data-testid="flex-template-modal">
            <p>Install a published zFairs template as a snapshot on this fair. Use as is keeps the structure locked. Make a copy if you want to edit pages and fields.</p>
            {(templates?.length ?? 0) === 0 && (
              <div className="alert alert-info">No published templates.</div>
            )}
            {templates?.map((t) => {
              const name = t.PublishedName || t.Name;
              const description = t.PublishedDescription || t.Description;
              return (
                <div className="list-item bumper-bottom" key={t.PublicId} data-testid={`flex-template-item-${t.PublicId}`}>
                  <div className="flex-between">
                    <div>
                      <strong>{name}</strong>
                      {t.IsRegistrationFlow && <span className="small bumper">Registration</span>}
                      {description && <div className="small" dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }} />}
                    </div>
                    <div className="btn-group">
                      <button
                        type="button"
                        className="btn btn-secondary btn-xs"
                        data-testid="flex-template-use-as-is"
                        disabled={!!busyId}
                        onClick={() => install(t.PublicId, 'as-is')}
                      >
                        Use as is
                      </button>
                      <button
                        type="button"
                        className="btn btn-default btn-xs"
                        data-testid="flex-template-copy"
                        disabled={!!busyId}
                        onClick={() => install(t.PublicId, 'copy')}
                      >
                        Make a copy
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Modal>
      )}
    </>
  );
};

export default FlexFlowAddFromTemplate;
